<!-- PedidosTabla.vue — Tabla desktop + cards móvil -->
<!-- Props: pedidos[], loading, paginación, sort -->
<!-- Emits: ver-detalle, editar-items, eliminar, cambiar-pagina, toggle-sort, cambiar-por-pagina -->
<template>
  <div>
    <!-- ══ TABLA ESCRITORIO ════════════════════════════════════ -->
    <!-- Umbral en xl (1200px), no lg (992px): las columnas ya se amontonaban
         a 1024px sin llegar a mostrarse todas con claridad. -->
    <div class="divisan-table-container shadow-lg d-none d-xl-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
        <p class="mt-3 text-cafe fw-bold small">Cargando pedidos...</p>
      </div>
      <table v-else class="table table-divisan table-pedidos align-middle mb-0">
        <colgroup>
          <col style="width: 8%" />
          <col style="width: 24%" />
          <col style="width: 10%" />
          <col style="width: 10%" />
          <col style="width: 10%" />
          <col style="width: 12%" />
          <col style="width: 13%" />
          <col style="width: 13%" />
        </colgroup>
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="numero_pedido"
                label="Pedido"
                type="number"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="cliente"
                label="Cliente"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="fecha_recibo"
                label="Recibido"
                type="date"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="fecha_entrega_real"
                label="Entregado"
                type="date"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="dias_restantes"
                label="Restantes"
                type="number"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="estatus_almacen"
                label="Almacén"
                :items="itemsParaFiltroEstatus"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                :modelValue="filtroEstatusSet"
                @update:modelValue="$emit('update:filtroEstatusSet', $event)"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="estatus_cliente"
                label="Cliente"
                :filterable="false"
                :items="pedidos"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="(e) => $emit('update:sort', e)"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in pedidos" :key="p.id">
            <td class="ps-4">
              <span class="badge-molde">{{ p.numero_pedido }}</span>
            </td>
            <td>
              <div class="cliente-cell">
                <span class="cliente-num">{{ p.cliente_numero }}</span>
                <span class="cliente-nombre">{{ p.cliente }}</span>
              </div>
            </td>
            <td class="fecha-texto">{{ formatFecha(p.fecha_recibo) }}</td>
            <td class="fecha-texto">{{ formatFecha(p.fecha_entrega_real) }}</td>
            <td class="text-center">
              <span :class="badgeDias(p.dias_restantes)">{{
                labelDias(p.dias_restantes)
              }}</span>
            </td>
            <td class="text-center">
              <span :class="badgeEstatusAlmacen(p.estatus_almacen)">{{
                labelEstatus(p.estatus_almacen)
              }}</span>
            </td>
            <td class="text-center">
              <span :class="badgeEstatusCliente(p.estatus_cliente)">{{
                labelEstatusCliente(p.estatus_cliente)
              }}</span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button
                  class="btn-circular"
                  title="Ver ítems"
                  @click="$emit('ver-detalle', p)"
                >
                  <i class="bi bi-list-ul text-dark"></i>
                </button>
                <button
                  class="btn-circular"
                  title="Editar ítems"
                  :disabled="!esEditable(p)"
                  :class="{ 'btn-circular-disabled': !esEditable(p) }"
                  @click="$emit('editar-items', p)"
                >
                  <i class="bi bi-pencil text-verde"></i>
                </button>
                <button
                  class="btn-circular"
                  title="Eliminar"
                  :disabled="!esEliminable(p)"
                  :class="{ 'btn-circular-disabled': !esEliminable(p) }"
                  @click="$emit('eliminar', p)"
                >
                  <i class="bi bi-trash text-danger"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!loading && pedidos.length === 0">
            <td colspan="8" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron pedidos
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info">
            {{ rangoInicio }}–{{ rangoFin }} de {{ totalRegistros }} pedidos
          </small>
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="$emit('cambiar-por-pagina', Number($event.target.value))"
            >
              <option v-for="n in opcionesPorPagina" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>

        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="$emit('cambiar-pagina', 1)"
            title="Primera"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="$emit('cambiar-pagina', paginaActual - 1)"
          >
            <i class="bi bi-chevron-left"></i>
          </button>

          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="$emit('cambiar-pagina', p)"
            >
              {{ p }}
            </button>
          </template>

          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="$emit('cambiar-pagina', paginaActual + 1)"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="$emit('cambiar-pagina', totalPaginas)"
            title="Última"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ══ CARDS MÓVIL / TABLET ════════════════════════════════ -->
    <div class="d-xl-none">
      <div
        v-for="p in pedidos"
        :key="'mob-' + p.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="badge-molde">{{ p.numero_pedido }}</span>
          <span class="cliente-num">{{ p.cliente_numero }}</span>
          <div class="d-flex gap-2">
            <span :class="badgeDias(p.dias_restantes)">{{
              labelDias(p.dias_restantes)
            }}</span>
          </div>
        </div>
        <div class="cliente-nombre mb-1">{{ p.cliente }}</div>
        <div class="fw-bold text-verde-oscuro mb-1">{{ p.cliente_numero }}</div>
        <div class="d-flex justify-content-between mb-2">
          <small class="text-cafe">Recibo: {{ formatFecha(p.fecha_recibo) }}</small>
          <small class="text-cafe"
            >Entrega: {{ formatFecha(p.fecha_entrega_real) }}</small
          >
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-nuevo" @click="$emit('ver-detalle', p)">
            <i class="bi bi-list-ul me-1"></i>Ver ítems
          </button>
          <button
            class="btn btn-sm btn-editar"
            @click="$emit('editar-items', p)"
            :disabled="!esEditable(p)"
          >
            <i class="bi bi-pencil me-1"></i>Editar
          </button>
          <button
            class="btn btn-sm btn-danger-divisan"
            @click="$emit('eliminar', p)"
            :disabled="!esEliminable(p)"
          >
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>
      <div
        v-if="!loading && pedidos.length === 0"
        class="text-center py-5 text-cafe opacity-50 fw-bold"
      >
        No se encontraron pedidos
      </div>

      <!-- Paginación móvil -->
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info">
            {{ rangoInicio }}–{{ rangoFin }} de {{ totalRegistros }} pedidos
          </small>
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="$emit('cambiar-por-pagina', Number($event.target.value))"
            >
              <option v-for="n in opcionesPorPagina" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="$emit('cambiar-pagina', paginaActual - 1)"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="$emit('cambiar-pagina', paginaActual + 1)"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { usePedidoBadges } from "./composables/usePedidoBadges.js";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

defineProps({
  pedidos: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  paginaActual: { type: Number, default: 1 },
  totalPaginas: { type: Number, default: 1 },
  totalRegistros: { type: Number, default: 0 },
  rangoInicio: { type: Number, default: 0 },
  rangoFin: { type: Number, default: 0 },
  paginasVisibles: { type: Array, default: () => [] },
  sortKey: { type: String, default: "" },
  sortDir: { type: String, default: "asc" },
  porPagina: { type: Number, default: 10 },
  opcionesPorPagina: { type: Array, default: () => [5, 10, 15, 20] },
  itemsParaFiltroEstatus: { type: Array, default: () => [] },
  filtroEstatusSet: { type: Set, default: () => new Set() },
});

defineEmits([
  "ver-detalle",
  "editar-items",
  "eliminar",
  "cambiar-pagina",
  "update:sort",
  "update:filtroEstatusSet",
  "cambiar-por-pagina",
]);

const {
  formatFecha,
  labelDias,
  badgeDias,
  badgeEstatusAlmacen,
  badgeEstatusCliente,
  labelEstatus,
  labelEstatusCliente,
} = usePedidoBadges();

function esEliminable(p) {
  return p.estatus_almacen !== "Cancelado" && p.estatus_almacen !== "Entregado";
}
function esEditable(p) {
  return p.estatus_almacen !== "Cancelado";
}
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-verde-oscuro {
  color: var(--d-green-dark);
}
.fecha-texto {
  color: var(--d-green);
  font-weight: 700;
  font-size: 0.9rem;
}
.cliente-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.cliente-num {
  font-family: var(--d-font-mono);
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--d-green);
}
.cliente-nombre {
  font-size: 0.74rem;
  color: var(--d-brown);
  font-weight: 700;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}

/*
 * Anchos fijos por columna (colgroup en el template) para que la tabla
 * no se estire por el texto largo de Cliente ni por los encabezados —
 * los títulos de columna se envuelven a 2 renglones en vez de forzar
 * el ancho con una sola línea.
 */
.table-pedidos {
  table-layout: fixed;
  width: 100%;
}
/* Encabezados a 2 renglones — anula el nowrap por defecto de
   ColumnFilterMenu (que sí conviene para el resto de las tablas, con
   nombres de columna más cortos). min-width:0 es necesario para que el
   texto realmente se envuelva dentro de un hijo flex en vez de
   desbordar la celda (comportamiento por defecto de flexbox). */
.table-pedidos :deep(.cfm) {
  white-space: normal;
  min-width: 0;
}
.table-pedidos :deep(.cfm-label) {
  white-space: normal;
  line-height: 1.2;
  min-width: 0;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
  padding: 10px 12px;
  font-weight: 800;
  text-transform: capitalize;
  white-space: normal;
  line-height: 1.2;
  vertical-align: middle;
  border-top: none;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 10px 12px;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.04);
}

.badge-molde {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
  padding: 4px 10px;
  border-radius: 6px;
  font-family: var(--d-font-mono);
  font-size: 0.78rem;
  font-weight: 600;
}
.badge-estatus {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 800;
  white-space: nowrap;
}
.badge-estatus.en_proceso {
  background: #e3f2fd;
  color: #1565c0;
}
.badge-estatus.listo {
  background: #e8f5e9;
  color: #2e7d32;
}
.badge-estatus.entregado {
  background: #e8f5e9;
  color: #1b5e20;
}
.badge-estatus.cancelado {
  background: #fce4ec;
  color: #880e4f;
}
.badge-estatus.atrasado {
  background: #fdecea;
  color: #c0392b;
}
.badge-estatus.en_tiempo {
  background: #e8f5e9;
  color: #2e7d32;
}
.badge-estatus.entrega_en_tiempo {
  background: #e0f2f1;
  color: #004d40;
}
.badge-estatus.entrega_con_atraso {
  background: #fff3e0;
  color: #e65100;
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-editar {
  background-color: #e8f5e9;
  color: var(--d-green-dark);
  border: 1px solid var(--d-green);
  font-weight: 600;
}
.btn-editar:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-danger-divisan {
  background-color: #fdecea;
  color: #c0392b;
  border: 1px solid #c0392b;
  font-weight: 600;
}
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
}
.btn-circular:hover {
  background: var(--d-green);
  color: var(--d-cream);
}
.btn-circular-disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-brown);
}

/* ── Paginación escritorio ── */
.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}

/* Select por página */
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}

/* Botones de navegación */
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}

.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}

.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

/* ── Paginación móvil ── */
.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
